/**
 * Client-side drop timing — the board/editor mirror of the planner's
 * timeline (backend planner/engine/slots/lay-slots.ts — keep in sync).
 * Phase 21: an enabled drop on the clock takes the next tick in its order
 * (start + k × interval), a pinned drop keeps its minute; a disabled drop
 * takes no time; endAtMin clamps with a flag, and so does the country's
 * window (a hard limit — `outsideWindow`; lifted by the container or the drop
 * — `sentOutside`, 14/10/2026). AFTER_PREVIOUS chains against
 * the nearest earlier ENABLED container OF ITS CAMPAIGN GROUP (`masterId`;
 * a group's first falls back to the window open). The Danish clock
 * (15/10/2026): start, end and pinned drops are Danish times — put on the
 * country's clock for the window's day with the planner's own
 * `onCountryClock` before anything is laid. A route test's first drop goes
 * before the window on purpose (the planner's keepFirstOutsideWindow, 19/10/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { type CountryClockDay, inCountryWindow, onCountryClock, type CountryWindow, ROUTE_TEST_KIND } from '@trafficpulse/shared';

import { planDay } from './clock/plan-day';

/** The country's window and — when the plan has a timezone — the day the Danish times land on. */
export type TimedWindow = CountryWindow & { day?: CountryClockDay };

/** The plan's window as the planner reads it, on today's run date unless one is given. */
export const planWindow = (
  plan: { windowStartMin: number; windowEndMin: number; recipientTimezone?: string | null },
  runDate?: string | null,
): TimedWindow => ({
  startMin: plan.windowStartMin,
  endMin: plan.windowEndMin,
  ...(plan.recipientTimezone ? { day: planDay({ recipientTimezone: plan.recipientTimezone, windowStartMin: plan.windowStartMin }, runDate) } : {}),
});

export interface TimedSlotInput {
  id: string | null;
  enabled: boolean;
  /** Phase 21 — an exact minute; null/absent = on the clock. */
  pinnedAtMin?: number | null;
  /** 14/10/2026 — this drop goes even outside the country's window. */
  sendOutsideWindow?: boolean;
}

export interface TimedContainerInput {
  id: string;
  enabled: boolean;
  startMode: string;
  gapMin: number;
  startAtMin: number | null;
  endAtMin: number | null;
  intervalMin: number;
  slots: TimedSlotInput[];
  /** The campaign group (its master config) "after previous" chains inside. */
  masterId?: string | null;
  /** 14/10/2026 — every drop of it goes outside the country's window too. */
  sendOutsideWindow?: boolean;
  /** ROUTE_TEST: its first enabled drop is sent even outside the window. */
  kind?: string;
}

export interface SlotTime {
  slotId: string | null;
  minute: number;
  /** True when endAtMin cuts this drop — it will not be planned. */
  clamped: boolean;
  /** True when the drop falls outside the country's window — it will not be planned. */
  outsideWindow?: boolean;
  /** 14/10/2026 — outside the window but sent: its container or the drop lifts the window. */
  sentOutside?: boolean;
  /** Phase 21 — the drop keeps its own minute. */
  pinned?: boolean;
  /** 20/10/2026 — a route test's drop: its window is the whole day, so it goes before the window on purpose, never "not sent". */
  routeTest?: boolean;
}

/** The stable key a draft slot is timed under: its id, else its position. */
export const slotKey = (slot: { id: string | null }, index: number): string =>
  slot.id ?? String(index);

export interface ContainerTimes {
  containerId: string;
  startMin: number;
  /** In the drops' order (look up by slot key); pinned ones at their minute. */
  slotTimes: SlotTime[];
  /** Last un-clamped drop minute; startMin when nothing plans. */
  lastPlannedMin: number;
  /** The end time on the country's clock (null = none). */
  endMin: number | null;
}

/** A drop that will be planned: not past the end, inside the window. */
export const isPlanned = (t: SlotTime): boolean => !t.clamped && !t.outsideWindow;

export function computeContainerTimes(
  ordered: TimedContainerInput[],
  window: TimedWindow,
): Map<string, ContainerTimes> {
  const out = new Map<string, ContainerTimes>();
  const ends = new Map<string, number>();
  for (const danish of ordered) {
    const container = window.day ? onCountryClock(danish, window.day) : danish;
    const chain = container.masterId ?? '';
    const startMin = resolveStart(container, window.startMin, ends.get(chain) ?? null);
    const slotTimes = layListTimes(container, startMin).map(({ lifted, ...t }) => {
      const outside = !t.clamped && !inCountryWindow(t.minute, window);
      const sent = outside && (lifted || container.sendOutsideWindow === true || t.routeTest === true);
      return { ...t, outsideWindow: outside && !sent, ...(sent ? { sentOutside: true } : {}) };
    });
    const live = slotTimes.filter(isPlanned).map((t) => t.minute);
    const lastPlannedMin = live.length > 0 ? Math.max(...live) : startMin;
    out.set(container.id, { containerId: container.id, startMin, slotTimes, lastPlannedMin, endMin: container.endAtMin });
    if (container.enabled) ends.set(chain, lastPlannedMin);
  }
  return out;
}

/** The clock and the pinned minutes — the engine's rule, row by row. */
function layListTimes(container: TimedContainerInput, startMin: number): Array<SlotTime & { lifted: boolean }> {
  const out: Array<SlotTime & { lifted: boolean }> = [];
  let tick = 0;
  const routeTest = container.kind === ROUTE_TEST_KIND;
  for (const slot of container.slots) {
    if (!slot.enabled) continue;
    const pinned = slot.pinnedAtMin !== null && slot.pinnedAtMin !== undefined;
    const minute = pinned ? (slot.pinnedAtMin as number) : startMin + container.intervalMin * tick++;
    const clamped = container.endAtMin !== null && minute > container.endAtMin;
    const mark = routeTest ? { routeTest: true } : {};
    out.push({ slotId: slot.id, minute, clamped, pinned, lifted: slot.sendOutsideWindow === true, ...mark });
  }
  return out;
}

function resolveStart(
  container: TimedContainerInput,
  windowStartMin: number,
  previousEnd: number | null,
): number {
  if (container.startMode === 'FIXED_TIME' && container.startAtMin !== null) {
    return container.startAtMin;
  }
  if (container.startMode === 'AFTER_PREVIOUS') {
    return previousEnd === null
      ? windowStartMin
      : previousEnd + container.gapMin;
  }
  return windowStartMin;
}
